import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import React from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import {
  clampYmdToToday,
  formatLocalYmd,
  getViewerUtcOffsetMinutes,
} from "../../lib/date-ymd.ts";
import { resolveStreamPlaybackUrl } from "../../lib/live-stream.ts";
import {
  FlashbackDatePicker,
  historyFilterFieldSx,
} from "./FlashbackDatePicker.tsx";
import type { FlashbackPlaybackLocationState } from "./FlashbackSectionTabs.tsx";
import { useRecordings } from "../../hooks/flashbackHooks/useRecordings.ts";
import { StatePanel } from "./StatePanel.tsx";
import type { Camera, Recording } from "../../api/flashbackApi/types.ts";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

type HistoryColumnAlign = "left" | "center" | "right";

const HISTORY_TABLE_COLUMNS: ReadonlyArray<{
  id: string;
  label: string;
  width: string;
  align?: HistoryColumnAlign;
}> = [
  { id: "name", label: "Name", width: "18%", align: "left" },
  { id: "created", label: "Created", width: "24%" },
  { id: "completed", label: "Completed", width: "24%" },
  { id: "duration", label: "Duration", width: "16%", align: "center" },
  { id: "action", label: "Action", width: "14%", align: "center" },
];

const historyTableSx = {
  tableLayout: "fixed",
  width: "100%",
} as const;

function historyHeaderCellSx(align: HistoryColumnAlign = "left") {
  return {
    px: 2,
    py: 1.5,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
    color: "var(--app-text-secondary)",
    borderBottom: "1px solid var(--app-border)",
    backgroundColor: "rgba(245, 247, 250, 0.98)",
    textAlign: align,
    whiteSpace: "nowrap" as const,
    position: "sticky" as const,
    top: 0,
    zIndex: 2,
  };
}

function historyBodyCellSx(align: HistoryColumnAlign = "left") {
  return {
    px: 2,
    py: 1.5,
    verticalAlign: "middle" as const,
    textAlign: align,
    borderBottom: "1px solid var(--app-border)",
    fontSize: 13,
    color: "var(--app-text-primary)",
  };
}

const historyNameCellSx = {
  ...historyBodyCellSx("left"),
  pl: 2,
  pr: 1,
};

const outlineBtnSx = {
  borderRadius: "6px",
  border: "1px solid var(--app-border)",
  backgroundColor: "var(--app-surface)",
  color: "var(--app-text-primary)",
  fontSize: 14,
  textTransform: "none" as const,
  px: 1.5,
  py: 0.75,
  "&:hover": {
    borderColor: "var(--app-primary)",
    backgroundColor: "var(--app-surface)",
  },
};

interface Props {
  cameras: Camera[];
  isLoadingCameras: boolean;
  camerasError: string | null;
  onRefreshCameras: () => void;
}

export function FlashbackHistoryPanel({
  cameras,
  isLoadingCameras,
  camerasError,
  onRefreshCameras,
}: Props): ReactElement {
  const navigate = useNavigate();
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const [date, setDate] = useState(() =>
    clampYmdToToday(formatLocalYmd(new Date())),
  );

  const handleDateChange = useCallback((ymd: string) => {
    setDate(clampYmdToToday(ymd));
  }, []);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    if (cameras.length === 0) {
      setSelectedCameraId(null);
      return;
    }
    setSelectedCameraId((current) => {
      if (current != null && cameras.some((c) => c.id === current))
        return current;
      return cameras[0].id;
    });
  }, [cameras]);

  useEffect(() => {
    setPage(1);
  }, [selectedCameraId, date, pageSize]);

  const recordingQuery = useMemo(
    () => ({
      date,
      utcOffsetMinutes: getViewerUtcOffsetMinutes(),
      sortBy: "startTime" as const,
      sortOrder: "desc" as const,
      page,
      pageSize,
    }),
    [date, page, pageSize],
  );

  const { data, isLoading, error, refresh } = useRecordings(
    selectedCameraId,
    recordingQuery,
  );

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = total === 0 ? 0 : Math.min(page * pageSize, total);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const openPlayback = (recording: Recording): void => {
    const state: FlashbackPlaybackLocationState = {
      date,
      startTime: recording.startTime,
      segmentId: recording.id,
      fromTab: "history",
    };
    navigate(`/cameras/${recording.cameraId}/playback`, { state });
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        px: 2,
        pb: 2,
        backgroundColor: "#F0F0F0",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#FFFFFF",
          border: "1px solid #E0E0E0",
          borderRadius: "12px",
          p: { xs: 2, sm: 2.5 },
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ gap: 1, mb: 2, pt: "10px", px: "8px" }}
        >
          <TextField
            select
            size="small"
            label="Camera"
            value={selectedCameraId ?? ""}
            onChange={(e) => setSelectedCameraId(String(e.target.value))}
            disabled={cameras.length === 0}
            sx={historyFilterFieldSx}
          >
            {cameras.map((camera) => (
              <MenuItem key={camera.id} value={camera.id}>
                {camera.name}
              </MenuItem>
            ))}
          </TextField>

          <FlashbackDatePicker value={date} onChange={handleDateChange} />
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {camerasError ? (
            <StatePanel
              variant="error"
              title="Couldn't load cameras"
              description={camerasError}
              action={
                <Button
                  type="button"
                  variant="text"
                  onClick={onRefreshCameras}
                  sx={outlineBtnSx}
                >
                  Retry
                </Button>
              }
            />
          ) : isLoadingCameras && cameras.length === 0 ? (
            <StatePanel
              title="Loading cameras…"
              description="Preparing the history browser."
            />
          ) : cameras.length === 0 ? (
            <StatePanel
              variant="empty"
              title="No cameras available"
              description="Add or enable cameras to browse recorded history."
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                px: "8px",
                pb: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {error ? (
                <StatePanel
                  variant="error"
                  title="Couldn't load recordings"
                  description={error}
                  action={
                    <Button
                      type="button"
                      variant="text"
                      onClick={refresh}
                      sx={outlineBtnSx}
                    >
                      Retry
                    </Button>
                  }
                />
              ) : isLoading && items.length === 0 ? (
                <StatePanel
                  title="Loading recordings…"
                  description="Fetching clips for the selected day."
                />
              ) : items.length === 0 ? (
                <StatePanel
                  variant="empty"
                  title="No recordings for this date"
                  description="Try another day or camera — only days with stored footage appear here."
                />
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    border: "1px solid #E0E0E0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <TableContainer
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflow: "auto",
                      WebkitOverflowScrolling: "touch",
                    }}
                  >
                    <Table stickyHeader size="small" sx={historyTableSx}>
                      <colgroup>
                        {HISTORY_TABLE_COLUMNS.map((column) => (
                          <col
                            key={column.id}
                            style={{ width: column.width }}
                          />
                        ))}
                      </colgroup>
                      <TableHead>
                        <TableRow>
                          {HISTORY_TABLE_COLUMNS.map((column) => (
                            <TableCell
                              key={column.id}
                              sx={historyHeaderCellSx(column.align)}
                            >
                              {column.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((recording) => (
                          <TableRow
                            key={recording.id}
                            hover
                            onClick={() => openPlayback(recording)}
                            sx={{
                              cursor: "pointer",
                              transition: "background-color 0.15s ease",
                              "&:hover": {
                                backgroundColor: "rgba(41, 50, 229, 0.04)",
                              },
                              "&:last-child td": {
                                borderBottom: "none",
                              },
                            }}
                          >
                            <TableCell sx={historyNameCellSx}>
                              <RecordingThumbnail recording={recording} />
                            </TableCell>
                            <TableCell sx={historyBodyCellSx()}>
                              <RecordingDateTime iso={recording.startTime} />
                            </TableCell>
                            <TableCell sx={historyBodyCellSx()}>
                              <RecordingDateTime iso={recording.endTime} />
                            </TableCell>
                            <TableCell sx={historyBodyCellSx("center")}>
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {formatDuration(recording.durationSeconds)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={historyBodyCellSx("center")}>
                              <RecordingPlayButton
                                label={recording.name}
                                onPlay={() => openPlayback(recording)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <HistoryTablePagination
                    page={page}
                    pageSize={pageSize}
                    total={total}
                    totalPages={totalPages}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                    onPageChange={setPage}
                    onPageSizeChange={(next) => {
                      setPageSize(next);
                      setPage(1);
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

interface HistoryTablePaginationProps {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  rangeStart: number;
  rangeEnd: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

function HistoryTablePagination({
  page,
  pageSize,
  total,
  totalPages,
  rangeStart,
  rangeEnd,
  onPageChange,
  onPageSizeChange,
}: HistoryTablePaginationProps): ReactElement {
  return (
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
        borderTop: "1px solid #E0E0E0",
        backgroundColor: "#F2F2F2",
        px: 1,
        py: 1,
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
            color: "rgba(0, 0, 0, 0.87)",
          }}
        >
          Items per page:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              height: 32,
              backgroundColor: "#FFFFFF",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E0E0E0",
              },
            }}
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <MenuItem
                key={option}
                value={option}
                sx={{ fontFamily: "Roboto, sans-serif" }}
              >
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
            color: "rgba(0, 0, 0, 0.6)",
          }}
        >
          {total > 0 ? `${rangeStart}-${rangeEnd} of ${total}` : "0 of 0"}
        </Typography>
      </Box>
      <Pagination
        count={totalPages}
        page={page}
        onChange={(_, value) => onPageChange(value)}
        color="primary"
        sx={{
          "& .MuiPaginationItem-root": {
            fontFamily: "Roboto, sans-serif",
            fontSize: "14px",
          },
          "& .Mui-selected": {
            backgroundColor: "#2932E5",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#1f28c7",
            },
          },
        }}
      />
    </Box>
  );
}

function RecordingThumbnail({
  recording,
}: {
  recording: Recording;
}): ReactElement {
  const [imgError, setImgError] = useState(false);
  const thumbnailSrc = recording.thumbnailUrl?.trim()
    ? resolveStreamPlaybackUrl(recording.thumbnailUrl)
    : null;
  const showThumbnail = Boolean(thumbnailSrc) && !imgError;

  return (
    <Box
      sx={{
        width: 108,
        aspectRatio: "16 / 9",
        borderRadius: "6px",
        overflow: "hidden",
        border: "1px solid var(--app-border)",
        backgroundColor: "#101010",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {showThumbnail ? (
        <Box
          component="img"
          src={thumbnailSrc!}
          alt={recording.name}
          loading="lazy"
          onError={() => setImgError(true)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <VideocamOutlinedIcon
          sx={{ fontSize: 22, color: "var(--app-text-secondary)" }}
        />
      )}
    </Box>
  );
}

function RecordingDateTime({ iso }: { iso: string }): ReactElement {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return (
      <Typography variant="body2" sx={{ fontSize: 13 }}>
        {iso}
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.35 }}
      >
        {format(d, "MMM d, yyyy")}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontSize: 12,
          lineHeight: 1.35,
          color: "var(--app-text-secondary)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {format(d, "h:mm:ss a")}
      </Typography>
    </Stack>
  );
}

function RecordingPlayButton({
  label,
  onPlay,
}: {
  label: string;
  onPlay: () => void;
}): ReactElement {
  return (
    <IconButton
      size="small"
      aria-label={`Play ${label}`}
      onClick={(e) => {
        e.stopPropagation();
        onPlay();
      }}
      sx={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        border: "1px solid var(--app-border)",
        backgroundColor: "var(--app-surface)",
        color: "var(--app-primary)",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.06)",
        transition:
          "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          backgroundColor: "var(--app-primary)",
          borderColor: "var(--app-primary)",
          color: "#fff",
          boxShadow: "0 2px 8px rgba(41, 50, 229, 0.28)",
        },
      }}
    >
      <PlayArrowIcon sx={{ fontSize: 18, ml: "2px" }} />
    </IconButton>
  );
}

function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}
